import { useQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

import { EditTargetsForm } from './types';

interface TargetBudgetInputProps {
  openingSlug: string;
  hookForm: UseFormReturn<EditTargetsForm>;
}

export function TargetBudgetInput({
  openingSlug,
  hookForm,
}: TargetBudgetInputProps) {
  const intl = useIntl();

  const [isBudgetReset, setIsBudgetReset] = useState(false);

  const {
    register,
    formState: { errors },
    watch,
    setValue,
  } = hookForm;

  const targetBudget = watch('budget_allotted');

  const { data: suggestedBudget, isFetching } = useQuery({
    queryKey: ['suggested-target-budget', openingSlug],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsSuggestedTargetBudget(
        openingSlug,
      ),
    select: data => data.target_budget,
  });

  const showSuggestedBudget = !!(
    suggestedBudget &&
    suggestedBudget > 0 &&
    suggestedBudget > (targetBudget ?? 0)
  );

  const handleResetBudget = () => {
    setValue('budget_allotted', suggestedBudget, {
      shouldDirty: true,
    });

    setIsBudgetReset(true);
  };

  return (
    <div className="flex flex-col gap-1">
      <Input
        isNumber
        label={intl.formatMessage({ defaultMessage: 'Target budget' })}
        {...register('budget_allotted', { valueAsNumber: true })}
        placeholderLabel="$"
        placeholder={intl.formatMessage({
          defaultMessage: 'Enter target budget',
        })}
        onReset={
          showSuggestedBudget || isBudgetReset ? handleResetBudget : undefined
        }
        error={errors.budget_allotted?.message}
      />

      <SuggestedTargetBudget
        isBudgetReset={isBudgetReset}
        suggestedBudget={suggestedBudget ?? 0}
        showSuggestedBudget={showSuggestedBudget}
        isFetching={isFetching}
      />
    </div>
  );
}

function SuggestedTargetBudget({
  isBudgetReset,
  suggestedBudget,
  showSuggestedBudget,
  isFetching,
}: {
  isBudgetReset: boolean;
  suggestedBudget: number;
  showSuggestedBudget: boolean;
  isFetching: boolean;
}) {
  const { formatCurrency } = useCurrencySymbol();

  if (isFetching) return <Skeleton className="mt-1 h-3 w-3/4" />;

  // If the suggested budget is not shown and the budget is not reset, return empty space -- so that the height of the input is not affected
  if (!showSuggestedBudget && !isBudgetReset)
    return <span className="h-4 w-full" />;

  return (
    <Typography
      variant="small1"
      className={isBudgetReset ? '!text-fx-green' : '!text-fx-neutral-600'}
    >
      {isBudgetReset ? (
        <FormattedMessage
          defaultMessage="✓ Suggested amount applied."
          description="Success message when suggested budget is applied"
        />
      ) : (
        <FormattedMessage
          defaultMessage="Based on current channel allocations, suggested budget is {suggestedBudget}"
          description="Description of the suggested budget"
          values={{
            suggestedBudget: formatCurrency(suggestedBudget ?? 0),
          }}
        />
      )}
    </Typography>
  );
}
